import { useCallback, useState } from 'react';
import { showErrorAlert } from 'utils/AlertUtils';
import * as labelApi from 'utils/labelApi';

/**
 * Powers the LABELS chip row in MerchantFilterDropdown - filtering a merchant
 * list *by* label. Not to be confused with `useMerchantLabels`, which assigns
 * labels to one merchant in the Edit Merchant form (RAT-1689).
 *
 * Labels are multi-select: each active label unions its merchants into the
 * pending selection, and turning one off only drops merchants no other active
 * label still covers.
 *
 * Role scoping is entirely the backend's job (RAT-1689) - `GET /labels` already
 * returns only the labels the caller may see, with a `merchantCount` counted
 * within their own scope, and `GET /labels/:id/merchants` returns the matching
 * subset. So a Dealer, Seller and Super Admin each get different labels, counts
 * and members from the same endpoints, and nothing here re-filters by role.
 */

// The chip row wants the whole (already role-scoped) taxonomy, not a page of
// it. Label Management owns real pagination; this is filter-only.
const LABEL_FILTER_LIMIT = 100;

// GET /labels/:id/merchants is paginated on purpose - a Global label can carry
// every merchant in the system - so walk every page rather than assuming one
// request covers it.
const LABEL_MERCHANTS_PAGE_SIZE = 100;

const fetchAllLabelMerchants = async (labelId) => {
  const first = await labelApi.getLabelMerchants(labelId, {
    page: 1,
    limit: LABEL_MERCHANTS_PAGE_SIZE,
  });
  const all = [...(first.list || [])];
  const totalPages = first.totalPages || 1;
  for (let page = 2; page <= totalPages; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const next = await labelApi.getLabelMerchants(labelId, {
      page,
      limit: LABEL_MERCHANTS_PAGE_SIZE,
    });
    all.push(...(next.list || []));
  }
  return all;
};

const useMerchantLabelFilter = () => {
  const [labels, setLabels] = useState([]);
  const [isFetchingLabels, setIsFetchingLabels] = useState(false);
  const [selectedLabelIds, setSelectedLabelIds] = useState([]);
  const [labelMerchantsCache, setLabelMerchantsCache] = useState({});
  const [isFetchingLabelMerchants, setIsFetchingLabelMerchants] =
    useState(false);

  const fetchLabels = useCallback(async () => {
    setIsFetchingLabels(true);
    try {
      // Busiest first, sorted server-side on the same scoped count that is
      // displayed - sorting the page client-side would only order the slice we
      // happened to receive.
      const data = await labelApi.listLabels({
        limit: LABEL_FILTER_LIMIT,
        sortKey: 'merchantCount',
        sortBy: -1,
      });
      // A label the caller can see but that covers none of their merchants
      // filters to nothing, so it is noise in a filter control - a Seller would
      // otherwise see the whole Global taxonomy sitting at (0).
      setLabels((data?.list || []).filter((label) => label.merchantCount > 0));
    } catch (error) {
      showErrorAlert(error?.message || 'Failed to load labels');
    } finally {
      setIsFetchingLabels(false);
    }
  }, []);

  /**
   * Members are cached exactly as the API returned them and narrowed at the
   * point of use - caching the narrowed result would go stale the moment the
   * caller's own scope changes (a region switch on the dashboard, a legal-info
   * switch in the invoice modal).
   */
  const loadMembers = useCallback(
    async (labelId) => {
      const cached = labelMerchantsCache[labelId];
      if (cached) return cached;
      setIsFetchingLabelMerchants(true);
      try {
        const rows = await fetchAllLabelMerchants(labelId);
        setLabelMerchantsCache((prev) => ({ ...prev, [labelId]: rows }));
        return rows;
      } catch (error) {
        showErrorAlert(
          error?.message || 'Failed to load merchants for this label',
        );
        return null;
      } finally {
        setIsFetchingLabelMerchants(false);
      }
    },
    [labelMerchantsCache],
  );

  /**
   * Object-based pickers (MerchantFilterDropdown): `availableMerchants` is the
   * list actually on screen. A label's members are intersected with it so a
   * chip can never select a merchant the user cannot see, and the objects kept
   * are the list's own, so every consumer sees one shape.
   */
  const handleLabelSelection = useCallback(
    async (
      label,
      pendingSelectedMerchant,
      setPendingSelectedMerchant,
      availableMerchants,
    ) => {
      const rows = await loadMembers(label.id);
      if (!rows) return;

      const narrow = (memberRows) => {
        const ids = new Set(memberRows.map((m) => m.merchantId));
        return (availableMerchants || []).filter((m) => ids.has(m.merchantId));
      };
      const members = narrow(rows);
      const isActive = selectedLabelIds.includes(label.id);

      if (isActive) {
        const nextActiveIds = selectedLabelIds.filter((id) => id !== label.id);
        const stillCoveredIds = new Set(
          nextActiveIds.flatMap((id) =>
            narrow(labelMerchantsCache[id] || []).map((m) => m.merchantId),
          ),
        );
        setSelectedLabelIds(nextActiveIds);
        setPendingSelectedMerchant(
          pendingSelectedMerchant.filter(
            (m) =>
              stillCoveredIds.has(m.merchantId) ||
              !members.some((lm) => lm.merchantId === m.merchantId),
          ),
        );
      } else {
        setSelectedLabelIds([...selectedLabelIds, label.id]);
        const existingIds = new Set(
          pendingSelectedMerchant.map((m) => m.merchantId),
        );
        const toAdd = members.filter((m) => !existingIds.has(m.merchantId));
        setPendingSelectedMerchant([...pendingSelectedMerchant, ...toAdd]);
      }
    },
    [selectedLabelIds, labelMerchantsCache, loadMembers],
  );

  const clearSelectedLabels = useCallback(() => setSelectedLabelIds([]), []);

  return {
    labels,
    isFetchingLabels,
    selectedLabelIds,
    isFetchingLabelMerchants,
    fetchLabels,
    handleLabelSelection,
    clearSelectedLabels,
  };
};

export default useMerchantLabelFilter;
